import React, { useState, useEffect } from 'react';
import { getAuthToken } from '../services/api';
import { 
  UserGroupIcon, 
  DocumentTextIcon, 
  CheckCircleIcon, 
  ClockIcon,
  ArrowTrendingUpIcon,
  PhoneIcon,
  BellIcon,
  CalendarIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';

interface DashboardProps {
  branchId?: string;
}

interface DashboardStats {
  leads: { total: number; new: number; hot: number };
  applications: { total: number; pending: number; approved: number };
  communications: { total: number; today: number };
  tasks: { total: number; overdue: number; today: number };
}

const Dashboard: React.FC<DashboardProps> = ({ branchId = 'kolkata' }) => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [recentLeads, setRecentLeads] = useState<any[]>([]);

  useEffect(() => {
    fetchDashboardData();
    fetchRecentLeads();
  }, [branchId]);

  const fetchRecentLeads = async () => {
    try {
      const url = branchId === 'all' 
        ? `${API_URL}/leads?limit=5`
        : `${API_URL}/leads?branchId=${branchId}&limit=5`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` }
      });
      
      if (response.data.success) {
        setRecentLeads(response.data.data.slice(0, 5));
      }
    } catch (error) {
      console.error('Error fetching recent leads:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Fetch overview data
      const response = await axios.get(`${API_URL}/reports/overview?branchId=${branchId}`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` }
      });
      
      const data = response.data.data;
      setStats({
        leads: {
          total: data.leads.total,
          new: data.leads.byStage.new || 0,
          hot: data.leads.byPriority.hot || 0
        },
        applications: {
          total: data.applications.total,
          pending: data.applications.byStatus.pending || 0,
          approved: data.applications.byStatus.approved || 0
        },
        communications: {
          total: data.communications.total,
          today: Math.floor(data.communications.total / 30) // Approximate
        },
        tasks: {
          total: 0,
          overdue: 0,
          today: 0
        }
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };
  // Show "Coming Soon" for Dhanbad and Mumbai
  if (branchId === 'dhanbad' || branchId === 'mumbai') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="mb-6">
            <svg className="w-24 h-24 mx-auto text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            {branchId === 'dhanbad' ? 'Dhanbad' : 'Mumbai'} Branch
          </h2>
          <p className="text-xl text-gray-600 mb-2">Coming Soon</p>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            This branch is currently in the pipeline. Stay tuned for updates as we expand our operations to {branchId === 'dhanbad' ? 'Dhanbad, Jharkhand' : 'Mumbai, Maharashtra'}.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const statsDisplay = stats ? [
    {
      name: 'Total Leads',
      value: stats.leads.total.toString(),
      subtext: `${stats.leads.new} new, ${stats.leads.hot} hot`,
      icon: UserGroupIcon,
      color: 'bg-blue-500',
      link: '/leads'
    },
    {
      name: 'Applications',
      value: stats.applications.total.toString(),
      subtext: `${stats.applications.pending} pending review`,
      icon: DocumentTextIcon,
      color: 'bg-green-500',
      link: '/applications'
    },
    {
      name: 'Communications',
      value: stats.communications.total.toString(),
      subtext: `${stats.communications.today} sent today`,
      icon: PhoneIcon,
      color: 'bg-purple-500',
      link: '/communications'
    },
    {
      name: 'Tasks',
      value: stats.tasks.total.toString(),
      subtext: `${stats.tasks.overdue} overdue`,
      icon: ClockIcon,
      color: 'bg-orange-500',
      link: '/tasks'
    }
  ] : [];

  const quickActions = [
    { name: 'Add New Lead', icon: UserGroupIcon, href: '/leads', color: 'bg-primary-600' },
    { name: 'Review Applications', icon: DocumentTextIcon, href: '/applications', color: 'bg-secondary-600' },
    { name: 'Schedule Follow-up', icon: PhoneIcon, href: '/communications', color: 'bg-green-600' },
    { name: 'View Analytics', icon: ArrowTrendingUpIcon, href: '/analytics', color: 'bg-purple-600' }
  ];

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'hot': return 'badge-hot';
      case 'warm': return 'badge-warm';
      case 'cold': return 'badge-cold';
      default: return 'badge-warm';
    }
  };

  const getStatusBadge = (stage: string) => {
    switch (stage) {
      case 'new': return 'status-new';
      case 'contacted': return 'status-contacted';
      case 'visit_scheduled': return 'status-scheduled';
      case 'applied': return 'status-applied';
      case 'admitted': return 'status-admitted';
      case 'rejected': return 'status-rejected';
      default: return 'status-new';
    }
  };

  return (
    <div className="space-y-8">
      {/* Blue Hero Snapshot Card */}
      <div className="blue-hero-card mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="space-y-2">
            <h1 className="text-4xl font-black text-white tracking-tight drop-shadow-sm">CRM Dashboard</h1>
            <p className="text-blue-100/90 text-lg flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5" />
              Overview of leads, applications, and admissions performance
            </p>
          </div>
          
          <div className="flex gap-4">
            <div className="bg-white/10 backdrop-blur-md px-5 py-3 rounded-2xl border border-white/20">
              <p className="text-xs text-blue-200 font-bold uppercase tracking-widest mb-1">Conversion Rate</p>
              <p className="text-3xl font-black text-white">
                {stats?.applications?.total && stats?.leads?.total 
                  ? Math.round((stats.applications.total / stats.leads.total) * 100) 
                  : '0'}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid - High Impact Glass Panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {(statsDisplay.length > 0 ? statsDisplay : [
          { name: 'Total Leads', value: '0', subtext: '0 new', icon: UserGroupIcon, color: 'bg-blue-500', link: '#' },
          { name: 'Applications', value: '0', subtext: '0 pending', icon: DocumentTextIcon, color: 'bg-green-500', link: '#' },
          { name: 'Communications', value: '0', subtext: '0 sent', icon: PhoneIcon, color: 'bg-purple-500', link: '#' },
          { name: 'Tasks', value: '0', subtext: '0 overdue', icon: ClockIcon, color: 'bg-orange-500', link: '#' }
        ]).map((stat) => (
          <a
            key={stat.name}
            href={stat.link}
            className="glass-card hover:scale-105"
          >
            <div className="flex items-center gap-5">
              <div className={`p-4 rounded-2xl shadow-lg ${stat.color} bg-opacity-10 text-blue-600`}>
                <stat.icon className="h-7 w-7" />
              </div>
              <div>
                <p className="text-xs font-black text-gray-400 uppercase tracking-widest">{stat.name}</p>
                <p className="text-3xl font-black text-gray-900 leading-none my-1">{stat.value}</p>
                <div className="flex items-center gap-1.5 text-xs font-bold text-blue-600/70 bg-blue-50/50 px-2 py-0.5 rounded-full w-fit">
                  <ArrowTrendingUpIcon className="h-3 w-3" />
                  {stat.subtext}
                </div>
              </div>
            </div>
          </a>
        ))}
      </div>

      {/* Desktop Quick Actions & Table Container */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Quick Actions - Milky Glass */}
        <div className="lg:col-span-4 h-full">
          <div className="glass-card p-8 h-full">
            <h2 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-3">
              <div className="w-2 h-6 bg-blue-600 rounded-full" />
              Quick Actions
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {quickActions.map((action) => (
                <a
                  key={action.name}
                  href={action.href}
                  className="group flex flex-col items-center justify-center p-5 rounded-3xl bg-blue-50 hover:bg-blue-600 transition-all duration-300 gap-3 border border-blue-100/50"
                >
                  <div className="p-3 bg-white rounded-2xl shadow-sm text-blue-600 group-hover:scale-110 transition-all">
                    <action.icon className="h-6 w-6" />
                  </div>
                  <span className="text-xs font-bold text-gray-800 group-hover:text-white text-center">{action.name}</span>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Leads - Milky Glass Table Panel */}
        <div className="lg:col-span-8 h-full">
          <div className="glass-card p-8 h-full">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-xl font-black text-gray-900 flex items-center gap-3">
                <div className="w-2 h-6 bg-indigo-500 rounded-full" />
                Recent High-Intent Leads
              </h2>
              <a href="/leads" className="flex items-center gap-2 group px-4 py-2 bg-slate-50 rounded-2xl text-blue-600 text-sm font-bold hover:bg-blue-600 hover:text-white transition-all">
                View Intelligence Hub
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </a>
            </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Parent Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Grade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentLeads.map((lead) => (
                <tr key={lead.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {lead.parentName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {lead.studentName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {lead.grade}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getPriorityBadge(lead.priority)}>
                      {lead.priority}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getStatusBadge(lead.stage)}>
                      {lead.stage.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {lead.createdAt && typeof lead.createdAt === 'string'
                      ? new Date(lead.createdAt).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })
                      : lead.createdAt?._seconds
                      ? new Date(lead.createdAt._seconds * 1000).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })
                      : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</div>
  );
};

export default Dashboard;